import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add,
  Refresh,
  Visibility,
  ContentCopy,
  CheckCircle
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import {
  AccountUserAuthMethod, ClientAccountSummary, ClientAccountCreate,
} from '../types/clientAccount';
import { ResellerSummary } from '../types/reseller';
import AuthMethodSelect from '../components/AuthMethodSelect';
import LoadingButton from '../components/LoadingButton';

// Filtro de GESTIÓN del listado: todas, solo directas (sin partner) o solo
// gestionadas por un partner. Espejo del query param `managed` del backend.
type ManagedFilter = 'all' | 'direct' | 'partner';

const emptyForm: ClientAccountCreate = {
  name: '',
  company: '',
  owner_email: '',
  owner_full_name: '',
  owner_password: '',
  notes: '',
  auth_method: 'password'
};

export const formatCredits = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : num.toFixed(2);
};

const ClientAccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<ClientAccountSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtros de gestión (server-side): tipo de gestión + un partner concreto.
  // Un partner concreto IMPLICA managed=partner, así que al elegirlo el selector
  // de tipo se sincroniza (y viceversa: volver a «Directas» limpia el partner).
  const [managedFilter, setManagedFilter] = useState<ManagedFilter>('all');
  const [resellerFilter, setResellerFilter] = useState<number | 'all'>('all');
  // Partners para poblar el selector concreto. Best-effort: si falla, el
  // selector de partner no aparece pero el filtro por tipo sigue funcionando.
  const [resellers, setResellers] = useState<ResellerSummary[]>([]);

  // Create account dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createData, setCreateData] = useState<ClientAccountCreate>({ ...emptyForm });
  // Cupo de empleados: como string para poder validar el input numérico (default 1)
  const [createMaxEmployees, setCreateMaxEmployees] = useState('1');
  // ALMACENAMIENTO pactado ya en el alta: GiB incluidos sin coste y techo de la
  // cuenta (0 = sin techo). VACÍOS por defecto y solo se envían si el admin
  // escribe algo: el formulario corto sigue creando la cuenta estándar (los
  // defaults de plataforma) y quien pacta otra cosa la fija sin pasar después
  // por el detalle. Como texto para distinguir «vacío» de «cero».
  const [createIncludedStorage, setCreateIncludedStorage] = useState('');
  const [createMaxStorage, setCreateMaxStorage] = useState('');

  // Generated password dialog (se muestra UNA vez)
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [createdAccountInfo, setCreatedAccountInfo] = useState<{
    account_name: string;
    owner_email: string;
    generated_password: string;
    // El backend intenta enviarla también por correo; si no salió (sin SMTP, o
    // fallo), se mantiene el texto de siempre: entrégala tú.
    email_sent: boolean;
  } | null>(null);
  // Alta SOLO-GOOGLE: no hay contraseña que enseñar, pero callarse sería peor
  // que el diálogo — el admin tiene que saber que la cuenta quedó creada y cómo
  // entrará su propietario.
  const [googleNotice, setGoogleNotice] = useState<string | null>(null);

  const loadAccounts = useCallback(async () => {
    try {
      setError('');
      // Un partner concreto manda reseller_id (y ya implica gestionadas por
      // partner en el backend); si no, se filtra por tipo. Sin filtro = todas.
      const filters: { managed?: ManagedFilter; reseller_id?: number } = {};
      if (resellerFilter !== 'all') {
        filters.reseller_id = resellerFilter;
      } else if (managedFilter !== 'all') {
        filters.managed = managedFilter;
      }
      const data = await apiService.getClientAccounts(filters);
      setAccounts(data);
    } catch (err: any) {
      console.error('Failed to load client accounts:', err);
      setError(err.detail || t('clientAccounts.loadError', 'No se pudieron cargar las cuentas de cliente'));
    } finally {
      setIsLoading(false);
    }
  }, [managedFilter, resellerFilter, t]);

  // Recarga en el montaje y cada vez que cambia un filtro (loadAccounts
  // depende de ambos): el filtrado es del backend, no en memoria.
  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Partners para el selector concreto (una sola vez). Best-effort: un fallo
  // deja la lista vacía y el selector de partner simplemente no se pinta.
  useEffect(() => {
    apiService.getResellers()
      .then(setResellers)
      .catch((err) => console.error('Failed to load resellers for filter:', err));
  }, []);

  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);
  const maxEmployeesValid = /^\d+$/.test(createMaxEmployees.trim()) && Number(createMaxEmployees) >= 1;
  // Almacenamiento: vacío es VÁLIDO (= no decidir aquí, se queda el default de
  // plataforma). Si se escribe, entero ≥ 0 — mismas cotas que el schema.
  const optionalGibValid = (raw: string) => raw.trim() === '' || /^\d+$/.test(raw.trim());
  const includedStorageValid = optionalGibValid(createIncludedStorage);
  const maxStorageValid = optionalGibValid(createMaxStorage);
  const canCreate =
    createData.name.trim() !== '' &&
    createData.owner_full_name.trim() !== '' &&
    isValidEmail(createData.owner_email) &&
    maxEmployeesValid &&
    includedStorageValid &&
    maxStorageValid;

  const handleCreateAccount = async () => {
    try {
      setCreateLoading(true);
      const authMethod = createData.auth_method || 'password';
      const payload: ClientAccountCreate = {
        name: createData.name.trim(),
        owner_email: createData.owner_email.trim(),
        owner_full_name: createData.owner_full_name.trim(),
        max_employees: Number(createMaxEmployees),
        auth_method: authMethod
      };
      if (createData.company?.trim()) payload.company = createData.company.trim();
      // Con SOLO GOOGLE no se manda contraseña ni aunque quedara escrita en el
      // formulario: no habría forma de usarla y el backend la ignoraría igual.
      if (authMethod !== 'google' && createData.owner_password?.trim()) {
        payload.owner_password = createData.owner_password.trim();
      }
      if (createData.notes?.trim()) payload.notes = createData.notes.trim();
      // Solo viajan si el admin los ha escrito: mandar el default de plataforma
      // convertiría un valor heredado (que cambia si cambia la plataforma) en
      // uno elegido y congelado en la cuenta.
      if (createIncludedStorage.trim() !== '') {
        payload.included_storage_gib = Number(createIncludedStorage.trim());
      }
      if (createMaxStorage.trim() !== '') {
        payload.max_storage_gib = Number(createMaxStorage.trim());
      }

      const response = await apiService.createClientAccount(payload);
      toast.success(t('clientAccounts.created', 'Cuenta de cliente creada'));
      setCreateDialogOpen(false);
      setCreateData({ ...emptyForm });
      setCreateMaxEmployees('1');
      setCreateIncludedStorage('');
      setCreateMaxStorage('');

      const ownerEmail = response.account.owner_email || payload.owner_email;
      if (response.generated_password) {
        setCreatedAccountInfo({
          account_name: response.account.name,
          owner_email: ownerEmail,
          generated_password: response.generated_password,
          email_sent: response.email_sent === true
        });
        setPasswordDialogOpen(true);
      } else if ((response.auth_methods || []).includes('google')) {
        setGoogleNotice(
          response.email_sent
            ? t(
                'clientAccounts.googleOnlyCreatedEmailed',
                '{{email}} entrará con su cuenta de Google. Le hemos enviado un correo de bienvenida: no hay contraseña que entregar.',
                { email: ownerEmail }
              )
            : t(
                'clientAccounts.googleOnlyCreated',
                '{{email}} entrará con su cuenta de Google: no hay contraseña que entregar.',
                { email: ownerEmail }
              )
        );
      }

      loadAccounts();
    } catch (err: any) {
      console.error('Failed to create client account:', err);
      toast.error(err.detail || t('clientAccounts.createError', 'No se pudo crear la cuenta'));
    } finally {
      setCreateLoading(false);
    }
  };

  // Sincroniza los dos selectores: elegir un partner concreto pone el tipo en
  // «De partners»; cambiar el tipo a algo que no sea «partner» limpia el partner
  // («Directas» + un partner concreto sería una contradicción sin resultados).
  const handleManagedChange = (value: ManagedFilter) => {
    setManagedFilter(value);
    if (value !== 'partner') setResellerFilter('all');
  };
  const handleResellerChange = (value: number | 'all') => {
    setResellerFilter(value);
    if (value !== 'all') setManagedFilter('partner');
  };

  // Contador del resultado ACTUAL (lo que hay en pantalla tras el filtro): total
  // y su desglose directas/partners. Con un filtro activo el texto se ajusta al
  // subconjunto que se está viendo, sin mentir sobre el total global.
  const directCount = accounts.filter((a) => !a.reseller_id).length;
  const partnerCount = accounts.length - directCount;
  const selectedResellerName =
    resellerFilter !== 'all'
      ? resellers.find((r) => r.id === resellerFilter)?.name ?? `#${resellerFilter}`
      : null;
  let resultLabel: string;
  if (selectedResellerName) {
    resultLabel = t('clientAccounts.countByPartner', '{{count}} cuentas de {{name}}', {
      count: accounts.length, name: selectedResellerName
    });
  } else if (managedFilter === 'direct') {
    resultLabel = t('clientAccounts.countDirect', '{{count}} cuentas directas', { count: accounts.length });
  } else if (managedFilter === 'partner') {
    resultLabel = t('clientAccounts.countPartners', '{{count}} cuentas de partners', { count: accounts.length });
  } else {
    resultLabel = t('clientAccounts.countAll', '{{total}} cuentas · {{direct}} directas · {{partner}} de partners', {
      total: accounts.length, direct: directCount, partner: partnerCount
    });
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('clientAccounts.title', 'Cuentas de cliente')}
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => loadAccounts()}
            size="small"
          >
            {t('common.refresh', 'Actualizar')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('clientAccounts.createAccount', 'Nueva cuenta')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filtros de GESTIÓN (server-side) + contador del resultado. Se pintan
          también con la lista vacía: si un filtro no deja nada, el usuario
          necesita el selector para volver atrás. */}
      {!isLoading && (
        <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="managed-filter-label">
              {t('clientAccounts.fields.management', 'Gestión')}
            </InputLabel>
            <Select
              labelId="managed-filter-label"
              label={t('clientAccounts.fields.management', 'Gestión')}
              value={managedFilter}
              onChange={(e) => handleManagedChange(e.target.value as ManagedFilter)}
            >
              <MenuItem value="all">{t('clientAccounts.filterAll', 'Todas')}</MenuItem>
              <MenuItem value="direct">{t('clientAccounts.filterDirect', 'Directas')}</MenuItem>
              <MenuItem value="partner">{t('clientAccounts.filterPartner', 'De partners')}</MenuItem>
            </Select>
          </FormControl>
          {/* Selector de partner concreto: solo si hay partners y el tipo no es
              «Directas» (donde no tendría sentido). */}
          {resellers.length > 0 && managedFilter !== 'direct' && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="reseller-filter-label">
                {t('clientAccounts.filterPartnerLabel', 'Partner')}
              </InputLabel>
              <Select
                labelId="reseller-filter-label"
                label={t('clientAccounts.filterPartnerLabel', 'Partner')}
                value={resellerFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  handleResellerChange(v === 'all' ? 'all' : Number(v));
                }}
              >
                <MenuItem value="all">
                  {t('clientAccounts.filterAllPartners', 'Todos los partners')}
                </MenuItem>
                {resellers.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Typography variant="body2" color="text.secondary">
            {resultLabel}
          </Typography>
        </Box>
      )}

      {/* Accounts table */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          {t('common.loading', 'Cargando...')}
        </Box>
      ) : accounts.length === 0 ? (
        <Card>
          <CardContent>
            <Typography align="center" color="text.secondary" py={4}>
              {(managedFilter !== 'all' || resellerFilter !== 'all')
                ? t('clientAccounts.noAccountsForFilter', 'Ninguna cuenta coincide con el filtro. Cambia la gestión o el partner.')
                : t('clientAccounts.noAccounts', 'Todavía no hay cuentas de cliente. Crea la primera con «Nueva cuenta».')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('clientAccounts.fields.name', 'Nombre')}</TableCell>
                <TableCell>{t('clientAccounts.fields.company', 'Empresa')}</TableCell>
                <TableCell>{t('clientAccounts.fields.owner', 'Propietario')}</TableCell>
                <TableCell>{t('clientAccounts.fields.management', 'Gestión')}</TableCell>
                <TableCell>{t('clientAccounts.fields.status', 'Estado')}</TableCell>
                <TableCell align="right">{t('clientAccounts.fields.balance', 'Saldo')}</TableCell>
                <TableCell>{t('clientAccounts.fields.createdAt', 'Creada')}</TableCell>
                <TableCell align="right">{t('clientAccounts.fields.actions', 'Acciones')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => (
                <TableRow
                  key={account.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/client-accounts/${account.id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {account.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{account.company || '—'}</TableCell>
                  <TableCell>{account.owner_email || '—'}</TableCell>
                  <TableCell>
                    {/* GESTIÓN: chip «Partner: {nombre}» enlazable al detalle
                        del partner, o «Directa». El chip del partner corta la
                        propagación para navegar a /partners/{id} en vez de a la
                        cuenta; el resto de la celda sigue abriendo la cuenta. */}
                    {account.reseller_id ? (
                      <Chip
                        label={t('clientAccounts.managedByPartner', 'Partner: {{name}}', {
                          name: account.reseller_name || `#${account.reseller_id}`
                        })}
                        color="info"
                        variant="outlined"
                        size="small"
                        clickable
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/partners/${account.reseller_id}`);
                        }}
                      />
                    ) : (
                      <Chip
                        label={t('clientAccounts.managedDirect', 'Directa')}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={account.status === 'active'
                        ? t('clientAccounts.status.active', 'Activa')
                        : account.status}
                      color={account.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {formatCredits(account.balance)} {t('clientAccounts.creditsUnit', 'créditos')}
                    </Typography>
                  </TableCell>
                  <TableCell>{format(new Date(account.created_at), 'dd/MM/yyyy')}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/client-accounts/${account.id}`);
                      }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create account dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('clientAccounts.createAccount', 'Nueva cuenta')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('clientAccounts.fields.name', 'Nombre') + ' *'}
            fullWidth
            variant="outlined"
            value={createData.name}
            onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('clientAccounts.fields.company', 'Empresa') + ' (' + t('common.optional', 'Opcional') + ')'}
            fullWidth
            variant="outlined"
            value={createData.company}
            onChange={(e) => setCreateData({ ...createData, company: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('clientAccounts.fields.ownerEmail', 'Email del propietario') + ' *'}
            type="email"
            fullWidth
            variant="outlined"
            value={createData.owner_email}
            onChange={(e) => setCreateData({ ...createData, owner_email: e.target.value })}
            error={createData.owner_email !== '' && !isValidEmail(createData.owner_email)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('clientAccounts.fields.ownerFullName', 'Nombre del propietario') + ' *'}
            fullWidth
            variant="outlined"
            value={createData.owner_full_name}
            onChange={(e) => setCreateData({ ...createData, owner_full_name: e.target.value })}
            sx={{ mb: 2 }}
          />
          {/* Cómo entrará el propietario. Va ANTES del campo de contraseña
              porque decide si ese campo pinta algo: con «Solo Google» no hay
              contraseña que fijar ni que enseñar. */}
          <AuthMethodSelect
            value={(createData.auth_method as AccountUserAuthMethod) || 'password'}
            onChange={(value) => setCreateData({ ...createData, auth_method: value })}
          />
          {createData.auth_method !== 'google' && (
            <TextField
              margin="dense"
              label={t('clientAccounts.fields.ownerPassword', 'Contraseña del propietario')}
              type="password"
              fullWidth
              variant="outlined"
              value={createData.owner_password}
              onChange={(e) => setCreateData({ ...createData, owner_password: e.target.value })}
              helperText={t('clientAccounts.fields.ownerPasswordHelp', 'Déjala vacía para generarla automáticamente (se mostrará una sola vez)')}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            margin="dense"
            label={t('clientAccounts.fields.maxEmployees', 'Máx. empleados')}
            type="number"
            fullWidth
            variant="outlined"
            value={createMaxEmployees}
            onChange={(e) => setCreateMaxEmployees(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            error={!maxEmployeesValid}
            helperText={!maxEmployeesValid
              ? t('clientAccounts.maxEmployeesInvalid', 'Debe ser un entero mayor o igual que 1')
              : t('clientAccounts.fields.maxEmployeesHelp', 'Cuántos empleados IA puede contratar esta cuenta')}
            sx={{ mb: 2 }}
          />
          {/* ALMACENAMIENTO de la cuenta, al lado del cupo porque son la misma
              decisión (cuánta capacidad le doy) y ambos se pueden cambiar luego
              en el detalle. Vacíos = defaults de plataforma: el alta normal no
              obliga a decidirlo. */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label={t('clientAccounts.fields.includedStorage', 'GiB incluidos sin coste')}
              type="number"
              fullWidth
              variant="outlined"
              value={createIncludedStorage}
              onChange={(e) => setCreateIncludedStorage(e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              error={!includedStorageValid}
              helperText={!includedStorageValid
                ? t('clientAccounts.storageIntegerInvalid', 'Debe ser un entero mayor o igual que 0')
                : t('clientAccounts.fields.includedStorageCreateHelp', 'Vacío = el incluido estándar de la plataforma')}
            />
            <TextField
              margin="dense"
              label={t('clientAccounts.fields.maxStorage', 'Límite de almacenamiento (GiB)')}
              type="number"
              fullWidth
              variant="outlined"
              value={createMaxStorage}
              onChange={(e) => setCreateMaxStorage(e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              error={!maxStorageValid}
              helperText={!maxStorageValid
                ? t('clientAccounts.storageIntegerInvalid', 'Debe ser un entero mayor o igual que 0')
                : t('clientAccounts.fields.maxStorageCreateHelp', 'Vacío o 0 = sin límite')}
            />
          </Box>
          <TextField
            margin="dense"
            label={t('clientAccounts.fields.notes', 'Notas') + ' (' + t('common.optional', 'Opcional') + ')'}
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={createData.notes}
            onChange={(e) => setCreateData({ ...createData, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('common.cancel', 'Cancelar')}</Button>
          <LoadingButton
            onClick={handleCreateAccount}
            loading={createLoading}
            disabled={!canCreate}
            variant="contained"
          >
            {t('common.create', 'Crear')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Alta SOLO-GOOGLE: no hay contraseña, pero sí algo que contar */}
      <Dialog
        open={googleNotice !== null}
        onClose={() => setGoogleNotice(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color="success" />
            {t('clientAccounts.createdTitle', 'Cuenta creada correctamente')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">{googleNotice}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoogleNotice(null)} variant="contained">
            {t('common.close', 'Cerrar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generated password dialog — se muestra UNA sola vez */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color="success" />
            {t('clientAccounts.createdTitle', 'Cuenta creada correctamente')}
          </Box>
        </DialogTitle>
        <DialogContent>
          {createdAccountInfo && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>{t('clientAccounts.passwordOnceTitle', 'Importante: guarda esta contraseña')}</AlertTitle>
                {t('clientAccounts.passwordOnceMessage', 'La contraseña no volverá a mostrarse. Cópiala y entrégasela al propietario de la cuenta.')}
                {/* Solo si el correo salió de verdad: si falló o no hay SMTP,
                    el mensaje de arriba sigue siendo la única instrucción válida. */}
                {createdAccountInfo.email_sent && (
                  <Box sx={{ mt: 1 }}>
                    {t('clientAccounts.passwordAlsoEmailed', 'También se ha enviado por correo a {{email}}', {
                      email: createdAccountInfo.owner_email
                    })}
                  </Box>
                )}
              </Alert>
              <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('clientAccounts.fields.name', 'Nombre')}
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  {createdAccountInfo.account_name}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('clientAccounts.fields.ownerEmail', 'Email del propietario')}
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  {createdAccountInfo.owner_email}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('clientAccounts.fields.generatedPassword', 'Contraseña generada')}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {createdAccountInfo.generated_password}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(createdAccountInfo.generated_password);
                      toast.success(t('clientAccounts.passwordCopied', 'Contraseña copiada al portapapeles'));
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)} variant="contained">
            {t('clientAccounts.passwordSaved', 'He guardado la contraseña')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientAccountsPage;
